import { component } from 'picoapp';

export default component(async (node) => {
  const { disablePageScroll, enablePageScroll } = await import(
    /* webpackChunkName: "scroll-lock" */
    'modules/scroll-lock'
  );

  const handleize = (str) => str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

  const { geolocatorCurrentCountry } = node.dataset;
  const geolocatorCurrentCountryTarget = node.querySelectorAll(
    '[data-geolocator-current-country-target]',
  );

  async function getCountry() {
    const response = await fetch('https://get.geojs.io/v1/ip/country.json');
    const body = await response.json();

    return {
      country: handleize(body.country),
      name: body.name,
    };
  }

  function cancelCountrySelection() {
    const closeGeolocatorPopup = document.querySelectorAll(
      '[data-geolocator-close]',
    );

    closeGeolocatorPopup.forEach((closeBtn) => {
      closeBtn.addEventListener('click', () => {
        node.classList.remove('geolocator--active');
        node.classList.remove('geolocator--active-no-delay');
        enablePageScroll([node, ...node.querySelectorAll('[data-scrollable]')]);

        const cookieName = 'GEOLOCATOR';
        const cookieValue = 'true';
        const date = new Date();
        date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
        const expires = `; expires=${date.toString()}`;
        document.cookie = `${cookieName}=${cookieValue}${expires}; path=/`;
      });
    });
  }

  async function getAndParseCountryData() {
    const countryData = await getCountry();
    const countriesScriptTag = node.querySelector('[data-countries]');
    let dataCountries: { countries: any[]; internationalMessage: string } = {
      internationalMessage: '',
      countries: [],
    };

    if (countriesScriptTag) {
      dataCountries = JSON.parse(countriesScriptTag.textContent);
    }

    const userCountry = dataCountries.countries.find(
      (country) => country.code === countryData.country,
    );

    return { countryData, dataCountries, userCountry };
  }

  async function init() {
    const { dataCountries, userCountry } = await getAndParseCountryData();
    const { currentCountryIsoCode } = theme.strings.geolocator;

    if (userCountry) {
      if (userCountry.code === currentCountryIsoCode) {
        // Country matches, do nothing
      } else {
        // Country doesn't match, open geolocator after 3 seconds
        node.classList.add('geolocator--active');
        setTimeout(() => {
          disablePageScroll([
            node,
            ...node.querySelectorAll('[data-scrollable]'),
          ]);
        }, 3000);
      }
    } else {
      // User's country not found
      const internationalCountry = dataCountries.countries.find(
        (country) => country.international === true,
      );

      if (internationalCountry) {
        // International country found, open geolocator
        node.classList.add('geolocator--active');
        setTimeout(() => {
          disablePageScroll([
            node,
            ...node.querySelectorAll('[data-scrollable]'),
          ]);
        }, 3000);
      }
    }
  }

  async function updateActiveCountry() {
    const { countryData, dataCountries, userCountry } = await getAndParseCountryData();
    const currentCountry = geolocatorCurrentCountry;

    geolocatorCurrentCountryTarget.forEach((target) => {
      target.textContent = currentCountry;
    });

    const geolocatorMessage = node.querySelector('[data-geolocator-message]');
    if (geolocatorMessage) {
      if (userCountry) {
        geolocatorMessage.textContent = geolocatorMessage.textContent.replace(
          '[country]',
          countryData.name,
        );
      } else {
        // User's country not found
        const internationalCountry = dataCountries.countries.find(
          (country) => country.international === true,
        );

        if (internationalCountry) {
          // International country found, display international message
          geolocatorMessage.textContent = dataCountries.internationalMessage;
        }
      }
    }
  }

  function closeGeolocator() {
    node.classList.remove('geolocator--active');
    node.classList.remove('geolocator--active-no-delay');
    enablePageScroll([node, ...node.querySelectorAll('[data-scrollable]')]);

    const cookieName = 'GEOLOCATOR';
    const cookieValue = 'true';
    const date = new Date();
    date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
    const expires = `; expires=${date.toString()}`;
    document.cookie = `${cookieName}=${cookieValue}${expires}; path=/`;
  }

  function findHrefLangLink(countryCode: string): string | null {
    const hrefLangTags = document.querySelectorAll('link[rel="alternate"][hreflang]');
    let targetHrefLang: HTMLLinkElement | null = null;

    if (countryCode === 'dk') {
      targetHrefLang = Array.from(hrefLangTags).find(
        (tag) => (tag as HTMLLinkElement).hreflang === 'x-default',
      ) as HTMLLinkElement | null;
    } else if (countryCode === 'eu') {
      targetHrefLang = Array.from(hrefLangTags).find(
        (tag) => (tag as HTMLLinkElement).hreflang === 'en-GB',
      ) as HTMLLinkElement | null;
    } else {
      const hrefLangCode = `en-${countryCode.toUpperCase()}`;
      targetHrefLang = Array.from(hrefLangTags).find(
        (tag) => (tag as HTMLLinkElement).hreflang === hrefLangCode,
      ) as HTMLLinkElement | null;
    }

    return targetHrefLang ? targetHrefLang.href : null;
  }

  async function updatePreferredCountry() {
    const { dataCountries, userCountry } = await getAndParseCountryData();
    const internationalCountry = dataCountries.countries.find(
      (country) => country.international === true,
    );

    const redirectButton = node.querySelector<HTMLButtonElement>(
      '[data-geolocator-redirect]',
    );

    if (redirectButton) {
      const currentCountryCode = redirectButton.getAttribute(
        'data-current-country',
      );
      if (userCountry && userCountry.code === currentCountryCode) {
        redirectButton.hidden = true;
        return;
      }

      const selectedCountry = userCountry || internationalCountry;

      if (selectedCountry) {
        const flagImgSrc = `https://flagcdn.com/${selectedCountry.code}.svg`;
        redirectButton.innerHTML = `
          <img src="${flagImgSrc}" class="geolocator__flag" alt="${selectedCountry.name} flag">
          <span class="ml-2 mt-0.5 whitespace-nowrap">
            ${theme.strings.geolocator.redirect.replace('{{ country }}', selectedCountry.name)}
          </span>
        `;

        redirectButton.addEventListener('click', (event) => {
          event.preventDefault();
          const selectedCountryCode = selectedCountry.code.toLowerCase();
          const hrefLangLink = findHrefLangLink(selectedCountryCode);
          if (hrefLangLink) {
            window.location.href = hrefLangLink;
          } else {
            const baseUrl = selectedCountry.url;
            if (baseUrl) {
              const url = new URL(baseUrl);
              window.location.href = url.origin;
            } else {
              closeGeolocator();
            }
          }
        });
      }
    }
  }

  async function changeLocation() {
    const countryForm = node.querySelector<HTMLFormElement>(
      '[data-country-form]',
    );
    const countryList = node.querySelector<HTMLSelectElement>(
      '[data-country-list]',
    );

    if (countryForm && countryList) {
      countryList.addEventListener('change', async (event: Event) => {
        const select = event.target as HTMLSelectElement;
        const selectedOption = select.options[select.selectedIndex];
        const selectedCountryCode = selectedOption.value.toLowerCase();
        const currentCountry = node.getAttribute('data-current-country');

        if (selectedCountryCode === currentCountry) {
          closeGeolocator();
        } else {
          const hrefLangLink = findHrefLangLink(selectedCountryCode);
          if (hrefLangLink) {
            window.location.href = hrefLangLink;
          } else {
            const baseUrl = selectedOption.getAttribute('data-url');
            if (baseUrl) {
              const url = new URL(baseUrl);
              window.location.href = url.origin;
            } else {
              closeGeolocator();
            }
          }
        }
      });
    }
  }

  function currencySelect() {
    const form = document.querySelector<HTMLFormElement>(
      '[data-currency-selector]',
    );
    if (form) {
      const selector = form.querySelector('select');
      if (selector) {
        selector.addEventListener('change', () => {
          form.submit();
        });
      }
    }
  }

  function handleToggles() {
    const toggles = document.querySelectorAll('[data-load="geolocator"]');

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        node.classList.add('geolocator--active-no-delay');
        disablePageScroll([
          node,
          ...node.querySelectorAll('[data-scrollable]'),
        ]);
      });
    });
    cancelCountrySelection();
  }

  function openGeolocatorPopup() {
    init();
  }

  function checkCookieAndInit() {
    const cookieName = 'GEOLOCATOR';
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.includes(cookieName));

    if (!cookieValue) {
      openGeolocatorPopup();
    }
  }

  updateActiveCountry();
  updatePreferredCountry();
  changeLocation();
  currencySelect();
  handleToggles();
  checkCookieAndInit();
});
